import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import Api from 'utils/api';

import styles from './PositionManagementTab.module.css';

const PositionManagementTab = () => {

  const navigate = useNavigate();

  const [positions, setPositions] = useState([]);
  const [allPermissionsGrouped, setAllPermissionsGrouped] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // 모달 관련 상태
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('add'); // 'add' 또는 'edit'
  const [editingPosition, setEditingPosition] = useState({
    positionName: '',
    positionDescription: '',
  });
  const [selectedPermissions, setSelectedPermissions] = useState([]);


  const fetchInitialData = useCallback(async () => {
    try {
      setLoading(true);

      const posData = await Api.get('/manager/position');

      if (posData?.unauthorized) {
        navigate('/admin');  // ⚡ React Router를 사용해서 깜빡임 없이 이동
        return;
      }

      const permData = await Api.get('/manager/position/permission');

      setPositions(posData.positionList || []);
      setAllPermissionsGrouped(permData.permissionList || []);
    } catch (err) {
      setError("정보를 불러오는 데 실패했습니다.");
    } finally {
      setLoading(false);
    }
  }, [navigate]);
  
  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  // 등록 버튼 클릭 시
  const handleAddClick = () => {
    setModalMode('add');
    setEditingPosition({ positionName: '', positionDescription: '' });
    setSelectedPermissions([]);
    setIsModalOpen(true);
  };

  // 수정 버튼 클릭 시
  const handleEditClick = (pos) => {
    setModalMode('edit');
    setEditingPosition({ ...pos });

    const currentKeys = [];
    if (pos.positoinPermissionList) {
      pos.positoinPermissionList.forEach(permObj => {
        if (permObj.permission) {
          const values = Object.values(permObj.permission);
          currentKeys.push(...values);
        }
      });
    }
    setSelectedPermissions(currentKeys);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingPosition({ positionName: '', positionDescription: '' });
    setSelectedPermissions([]);
  };

  const handlePermissionToggle = (enumKey) => {
    setSelectedPermissions(prev =>
      prev.includes(enumKey)
        ? prev.filter(key => key !== enumKey)
        : [...prev, enumKey]
    );
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditingPosition(prev => ({ ...prev, [name]: value }));
  };

  // 저장 (등록/수정 분기 처리)
  const handleSave = async () => {
    // 유효성 검사
    if (!editingPosition.positionName.trim() || !editingPosition.positionDescription.trim()) {
      return alert("직책명과 설명을 모두 입력해주세요.");
    }

    try {
      let response;

      if (modalMode === 'edit') {
        // 수정
        const requestBody = {
          positionId: editingPosition.positionId,
          positionName: editingPosition.positionName,
          positionDescription: editingPosition.positionDescription,
          permissionList: selectedPermissions
        };

        response = await Api.patch('/manager/position', requestBody);

      } else {
        // 등록
        const requestBody = {
          positionName: editingPosition.positionName,
          positionDescription: editingPosition.positionDescription,
          permissionList: selectedPermissions
        };

        response = await Api.post('/manager/position', requestBody);
      }

      if (!response) return;

      alert(modalMode === 'edit' ? "직책 수정이 완료되었습니다." : "직책 등록이 완료되었습니다.");

      fetchInitialData();
      closeModal();

    } catch (error) {
      console.error("통신 오류:", error);
      alert("서버와 통신 중 오류가 발생했습니다.");
    }
  };

  const handleDelete = async (pos) => {
    if (!window.confirm(`[${pos.positionName}] 직책을 정말 삭제하시겠습니까?`)) return;

    const data = await Api.delete(`/manager/position?positionId=${pos.positionId}`)

    if (data.code === 200) {
      alert("성공적으로 삭제되었습니다.");
      fetchInitialData(); // 목록 새로고침
    } else {
      alert(`삭제 실패: ${data.message || '오류가 발생했습니다.'}`);
    }


  };

  if (loading) return <div className={styles.centerMsg}>정보를 불러오는 중...</div>;
  if (error) return <div className={styles.centerMsg}>{error}</div>;

  return (
    <div className={styles.container}>
      <div className={styles.topBar}>
        <div className={styles.countInfo}>
          등록된 직책 <span className={styles.countNumber}>{positions.length}</span>
        </div>
        <button className={styles.addBtn} onClick={handleAddClick}>+ 직책 등록</button>
      </div>

      <div className={styles.positionGrid}>
        {positions.map((pos) => (
          <div key={pos.positionId} className={styles.card}>
            <div className={styles.cardHeader}>
              <span className={styles.posName}>{pos.positionName}</span>
            </div>
            <p className={styles.posDesc}>{pos.positionDescription}</p>

            <div className={styles.permissionSection}>
              <span className={styles.permissionLabel}>할당된 권한</span>
              <div className={styles.permissionWrapper}>
                {pos.positoinPermissionList?.map((perm) => (
                  Object.keys(perm.permission || {}).map(key => (
                    <span key={key} className={styles.badge}>{key}</span>
                  ))
                ))}
              </div>
            </div>

            <div className={styles.footer}>
              <button className={styles.editBtn} onClick={() => handleEditClick(pos)}>수정</button>
              <button className={styles.deleteBtn} onClick={() => handleDelete(pos)}>삭제</button>
            </div>
          </div>
        ))}
      </div>

      {isModalOpen && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <h3>{modalMode === 'add' ? '새 직책 등록' : '직책 및 권한 수정'}</h3>
              <button className={styles.closeModalBtn} onClick={closeModal}>✕</button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.inputGroup}>
                <label>직책명</label>
                <input
                  type="text"
                  name="positionName"
                  className={styles.modalInput}
                  value={editingPosition?.positionName || ''}
                  onChange={handleInputChange}
                />
              </div>
              <div className={styles.inputGroup}>
                <label>설명</label>
                <textarea
                  name="positionDescription"
                  className={styles.modalTextarea}
                  value={editingPosition?.positionDescription || ''}
                  onChange={handleInputChange}
                  rows="2"
                />
              </div>

              <div className={styles.permissionEditor}>
                <label className={styles.editorLabel}>권한 설정</label>
                <div className={styles.categoryContainer}>
                  {allPermissionsGrouped.map((categoryObj) => (
                    <div key={categoryObj.permissionName} className={styles.categoryBlock}>
                      <div className={styles.categoryTitle}>{categoryObj.permissionName}</div>
                      <div className={styles.checkboxGrid}>
                        {categoryObj.permissionList.map((permMap) => {
                          const description = Object.keys(permMap)[0];
                          const enumKey = permMap[description];
                          return (
                            <label key={enumKey} className={styles.checkboxItem}>
                              <input
                                type="checkbox"
                                className={styles.checkboxInput}
                                checked={selectedPermissions.includes(enumKey)}
                                onChange={() => handlePermissionToggle(enumKey)}
                              />
                              <span className={styles.checkboxLabel}>{description}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.cancelBtn} onClick={closeModal}>취소</button>
              <button className={styles.saveBtn} onClick={handleSave}>
                {modalMode === 'add' ? '등록하기' : '수정완료'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PositionManagementTab;